import { webrtcManager } from '../services/webrtc';
import { useCallStore } from '../store/callStore';
import type { CallEndInfo } from './callTypes';
import { markCallAsActive } from './callLifecycle';

type CreateCallPeerHandlersParams = {
  handleCallEnd: (info?: CallEndInfo) => void;
};

export function createCallPeerHandlers(params: CreateCallPeerHandlersParams) {
  let disconnectedTimer: number | null = null;
  let autoIceRecoveryTimer: number | null = null;
  let autoIceRecoveryCallSessionId: string | null = null;
  let resumeRecoveryTimer: number | null = null;

  const disconnectedGraceMs = 30_000;
  const hiddenDisconnectedGraceMs = 90_000;

  const clearDisconnectedTimer = () => {
    if (disconnectedTimer !== null) {
      window.clearTimeout(disconnectedTimer);
      disconnectedTimer = null;
    }
  };

  const clearAutoIceRecoveryTimer = () => {
    if (autoIceRecoveryTimer !== null) {
      window.clearTimeout(autoIceRecoveryTimer);
      autoIceRecoveryTimer = null;
    }
    autoIceRecoveryCallSessionId = null;
  };

  const clearResumeRecoveryTimer = () => {
    if (resumeRecoveryTimer !== null) {
      window.clearTimeout(resumeRecoveryTimer);
      resumeRecoveryTimer = null;
    }
  };

  const scheduleAutoIceRecovery = (callSessionId: string) => {
    const normalized = String(callSessionId || '').trim();
    if (!normalized) return;
    if (autoIceRecoveryCallSessionId === normalized) return;

    clearAutoIceRecoveryTimer();
    autoIceRecoveryCallSessionId = normalized;
    autoIceRecoveryTimer = window.setTimeout(() => {
      autoIceRecoveryTimer = null;
      const state = useCallStore.getState();
      const activeCall = state.activeCall;
      if (!activeCall || activeCall.callSessionId !== normalized) {
        autoIceRecoveryCallSessionId = null;
        return;
      }
      if (state.connectionState === 'connected') {
        autoIceRecoveryCallSessionId = null;
        return;
      }

      void webrtcManager.restartIce({ forceRelay: false, refreshTurnCredentials: true })
        .catch(() => {})
        .finally(() => {
          autoIceRecoveryCallSessionId = null;
        });
    }, 3500);
  };

  const scheduleResumeRecovery = (source: string) => {
    clearResumeRecoveryTimer();
    resumeRecoveryTimer = window.setTimeout(() => {
      resumeRecoveryTimer = null;

      if (document.visibilityState !== 'visible') return;

      const state = useCallStore.getState();
      const activeCall = state.activeCall;
      if (!activeCall?.callSessionId) return;

      const localAudioState = webrtcManager.getLocalAudioTrackState();
      const localAudioNeedsRecovery = !webrtcManager.hasHealthyLocalAudioTrack() || localAudioState.muted;
      if (localAudioNeedsRecovery) {
        void webrtcManager.ensureLocalAudioTrackHealthy(`resume:${source}`).catch(() => {});
      }

      if (state.connectionState !== 'connected') {
        void webrtcManager.restartIce({ forceRelay: false, refreshTurnCredentials: true }).catch(() => {});
      }
    }, 1200);
  };

  const handleVisibilityRecovery = () => {
    if (document.visibilityState !== 'visible') return;
    scheduleResumeRecovery('visibilitychange');
  };

  const handlePageShowRecovery = () => {
    scheduleResumeRecovery('pageshow');
  };

  const handleFocusRecovery = () => {
    if (document.visibilityState !== 'visible') return;
    scheduleResumeRecovery('focus');
  };

  const install = () => {
    webrtcManager.setCallbacks({
      onRemoteStream: () => {},
      onConnectionStateChange: (state) => {
        useCallStore.getState().dispatchCallState({
          type: 'set-connection-state',
          state
        });

        clearDisconnectedTimer();

        if (state === 'connected') {
          markCallAsActive(useCallStore.getState().dispatchCallState);
        } else if (state === 'connecting') {
          useCallStore.getState().dispatchCallState({
            type: 'call/active-updated',
            patch: {
              connectionPhase: 'connecting_media'
            }
          });
        } else if (state === 'failed' || state === 'closed') {
          params.handleCallEnd({ reason: `pc_${state}`, source: 'webrtc' });
        } else if (state === 'disconnected') {
          useCallStore.getState().dispatchCallState({
            type: 'call/active-updated',
            patch: {
              connectionPhase: 'reconnecting'
            }
          });
          const graceMs = document.visibilityState === 'visible'
            ? disconnectedGraceMs
            : hiddenDisconnectedGraceMs;
          disconnectedTimer = window.setTimeout(() => {
            const current = useCallStore.getState();
            if (current.connectionState === 'disconnected') {
              params.handleCallEnd({ reason: 'disconnected_timeout', source: 'webrtc' });
            }
          }, graceMs);
        }
      },
      onIceConnectionStateChange: (iceState) => {
        if (iceState === 'connected' || iceState === 'completed') {
          markCallAsActive(useCallStore.getState().dispatchCallState);
        } else if (iceState === 'checking') {
          useCallStore.getState().dispatchCallState({
            type: 'call/active-updated',
            patch: {
              connectionPhase: 'finding_route'
            }
          });
        } else if (iceState === 'disconnected') {
          useCallStore.getState().dispatchCallState({
            type: 'call/active-updated',
            patch: {
              connectionPhase: 'reconnecting'
            }
          });
        }
      },
      onCallEnded: () => {
        params.handleCallEnd({ reason: 'webrtc_onCallEnded', source: 'webrtc' });
      },
      onSecurityVerified: (verified) => {
        useCallStore.getState().dispatchCallState({
          type: 'call/active-updated',
          patch: {
            securityVerified: verified
          }
        });
      }
    });

    document.addEventListener('visibilitychange', handleVisibilityRecovery);
    window.addEventListener('pageshow', handlePageShowRecovery);
    window.addEventListener('focus', handleFocusRecovery);
  };

  const cleanup = () => {
    clearDisconnectedTimer();
    clearAutoIceRecoveryTimer();
    clearResumeRecoveryTimer();
    document.removeEventListener('visibilitychange', handleVisibilityRecovery);
    window.removeEventListener('pageshow', handlePageShowRecovery);
    window.removeEventListener('focus', handleFocusRecovery);
  };

  return {
    install,
    cleanup,
    scheduleAutoIceRecovery
  };
}
